/**
 * @file Footer.js
 * @module src/js/components/Footer
 * @description
 * **Footer component displaying copyright, version, visitor count, and developer link.**
 * - Shows current year and design credit.
 * - Displays application version and last deployed date from environment variables.
 * - Fetches and shows total visitor count since 2025-03-21.
 * - Provides a developer zone link for logged-in users.
 *
 * @version
 * <p>
 *  2025-11-28: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useEffect, useState } from "react";
import { useUser } from "../contexts/UserProvider";
import useFetchFromBackend from "../hooks/useFetchFromBackend";
import { NewTab } from "./UtilityComponents";

export default function Footer() {
    const { uid } = useUser();
    const fetchFromBackend = useFetchFromBackend();
    const [visitorCount, setVisitorCount] = useState(null);

    useEffect(() => {
        async function fetchData() {
            const data = await fetchFromBackend({ request: "/analytics/visitors" });
            setVisitorCount(data.visitors);
        }
        fetchData();
    }, [fetchFromBackend]);

    return (
        <footer id="footer">
            <ul className="copyright">
                <li>Copyright&copy;{new Date().getFullYear()} Electronic Design Automation Laboratory. All rights reserved.</li>
                <li>Design: <NewTab href="https://html5up.net">HTML5 UP</NewTab></li>
            </ul>
            <ul className="copyright">
                <li>Version: {process.env.REACT_APP_VERSION ? `v${process.env.REACT_APP_VERSION}` : "N/A"}</li>
                <li>Last updated on {process.env.REACT_APP_LAST_DEPLOYED || "N/A"}</li>
                <li>{visitorCount === null ? "N/A" : visitorCount} visitors since 2025-03-21</li>
                {uid !== "Guest" && (
                    <li><NewTab href='https://eda.ee.ntu.edu.tw/docs/index.html' requireAuth={true}>Developer Zone (NTU IP Required)</NewTab></li>
                )}
            </ul>
        </footer>
    );
}
